import { useCallback, useEffect, useRef, useState } from 'react';

const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const AUTO_RELOAD_MS = 20 * 1000;

async function fetchBuildSignature() {
  const res = await fetch(`/asset-manifest.json?t=${Date.now()}`, {
    cache: 'no-store',
    headers: { 'Cache-Control': 'no-cache' },
  });
  if (!res.ok) return null;
  const manifest = await res.json();
  const main = manifest?.files?.['main.js']
    || (Array.isArray(manifest?.entrypoints)
      ? manifest.entrypoints.find((entry) => /main\.[a-f0-9]+\.js$/i.test(entry))
      : null);
  if (main) return String(main);
  const files = manifest?.files && typeof manifest.files === 'object' ? manifest.files : {};
  return JSON.stringify(files);
}

/**
 * Detects when Netlify publishes a new frontend build while a tab still runs old JS.
 * Production only — dev server has no stable asset-manifest signature.
 */
export function useDeployUpdateCheck({
  enabled = process.env.NODE_ENV === 'production',
  autoReloadMs = AUTO_RELOAD_MS,
} = {}) {
  const [updateReady, setUpdateReady] = useState(false);
  const signatureRef = useRef(null);
  const reloadTimerRef = useRef(null);

  const reloadNow = useCallback(() => {
    if (typeof window === 'undefined') return;
    window.location.reload();
  }, []);

  const scheduleAutoReload = useCallback(() => {
    if (reloadTimerRef.current != null) return;
    reloadTimerRef.current = window.setTimeout(() => {
      reloadNow();
    }, autoReloadMs);
  }, [autoReloadMs, reloadNow]);

  const checkForUpdate = useCallback(async () => {
    if (!enabled || updateReady) return;
    try {
      const signature = await fetchBuildSignature();
      if (!signature) return;
      if (signatureRef.current == null) {
        signatureRef.current = signature;
        return;
      }
      if (signature !== signatureRef.current) {
        setUpdateReady(true);
        if (document.visibilityState === 'hidden') {
          reloadNow();
          return;
        }
        scheduleAutoReload();
      }
    } catch (_err) {
      // Offline or transient fetch failure — try again on the next interval.
    }
  }, [enabled, reloadNow, scheduleAutoReload, updateReady]);

  useEffect(() => {
    if (!enabled) return undefined;

    checkForUpdate();
    const intervalId = window.setInterval(checkForUpdate, CHECK_INTERVAL_MS);

    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      checkForUpdate();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibility);
      if (reloadTimerRef.current != null) {
        window.clearTimeout(reloadTimerRef.current);
      }
    };
  }, [checkForUpdate, enabled]);

  return { updateReady, reloadNow };
}
